import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Flag } from 'lucide-react';
import { useI18n } from '../../../i18n';
import { sceneState } from './loginShared';

/*
 * Fixed overlays that live through the whole scroll journey, filling the
 * space between story cards: a live telemetry HUD and a progress rail.
 * Desktop-only, pointer-events-none, driven without React re-renders.
 */

/* Bottom-left racing telemetry: live speed (mirrored from the scene),
 * current lap and best-lap reference. */
const FINISH_TIME = 42.318;

function formatLapTime(seconds) {
  const whole = Math.floor(seconds);
  const ms = Math.floor((seconds - whole) * 1000);
  return `00:${String(whole).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

export function RaceHud() {
  const { t } = useI18n();
  const speedRef = useRef(null);
  const lapRef = useRef(null);
  const timerRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.08, 0.14, 0.88, 0.97], [0, 0, 1, 1, 0]);

  useEffect(() => {
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const p = sceneState.scroll;
      if (speedRef.current) {
        speedRef.current.textContent = String(Math.max(0, Math.round(sceneState.hudSpeed * 11)));
      }
      if (lapRef.current) {
        const lap = p < 0.32 ? 1 : p < 0.5 ? 2 : p < 0.7 ? 3 : 4;
        lapRef.current.textContent = `${lap} / 4`;
      }
      if (timerRef.current) {
        // Lap clock runs with the journey and freezes on the PB at the flag
        const done = p >= 0.83;
        const lapTime = done ? FINISH_TIME : FINISH_TIME * Math.min(Math.max((p - 0.12) / 0.71, 0), 1);
        timerRef.current.textContent = formatLapTime(lapTime);
        timerRef.current.style.color = done ? 'var(--accent)' : 'var(--text-primary)';
      }
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <motion.div
      style={{ opacity }}
      className="hidden lg:flex fixed bottom-6 left-8 z-20 pointer-events-none"
    >
      <div className="glass-card flex items-end gap-5 px-5 py-3.5">
        <div className="flex flex-col">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] mb-1" style={{ color: 'var(--text-muted)' }}>
            {t('Prędkość')}
          </span>
          <span className="flex items-baseline gap-1.5">
            <span ref={speedRef} className="font-mono text-4xl font-black leading-none tabular-nums" style={{ color: 'var(--text-primary)' }}>
              0
            </span>
            <span className="text-[10px] font-bold" style={{ color: 'var(--accent)' }}>km/h</span>
          </span>
        </div>
        <div className="w-px h-10" style={{ background: 'var(--glass-border)' }} />
        <div className="flex flex-col">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] mb-1" style={{ color: 'var(--text-muted)' }}>
            {t('Okrążenie')}
          </span>
          <span ref={lapRef} className="font-mono text-xl font-black leading-none" style={{ color: 'var(--text-primary)' }}>
            1 / 4
          </span>
        </div>
        <div className="w-px h-10" style={{ background: 'var(--glass-border)' }} />
        <div className="flex flex-col">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] mb-1" style={{ color: 'var(--text-muted)' }}>
            {t('Czas okrążenia')}
          </span>
          <span ref={timerRef} className="font-mono text-xl font-black leading-none tabular-nums" style={{ color: 'var(--text-primary)' }}>
            00:00.000
          </span>
        </div>
      </div>
    </motion.div>
  );
}

const RAIL_STOPS = [
  { at: 0.18, label: '01' },
  { at: 0.38, label: '02' },
  { at: 0.58, label: '03' },
  { at: 0.78, label: '04' },
  { at: 0.97, label: null }, // meta — flag icon
];

function RailStop({ progress, at, label }) {
  const active = useTransform(progress, [at - 0.04, at], [0, 1]);
  const opacity = useTransform(active, v => 0.35 + v * 0.65);
  const scale = useTransform(active, v => 1 + v * 0.25);
  return (
    <motion.div style={{ opacity, scale }} className="flex items-center gap-2">
      <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ background: 'var(--accent)', boxShadow: '0 0 8px var(--accent-glow)' }}
      />
      {label ? (
        <span className="text-[10px] font-black tracking-widest" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </span>
      ) : (
        <Flag size={12} style={{ color: 'var(--accent)' }} />
      )}
    </motion.div>
  );
}

/* Right-edge journey rail: a vertical track that fills as you scroll,
 * with stops for each story beat and the finish flag. */
export function ProgressRail() {
  const { scrollYProgress } = useScroll();
  const fill = useTransform(scrollYProgress, [0.08, 0.97], [0, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.08, 0.14, 0.97, 1], [0, 0, 1, 1, 0.5]);

  return (
    <motion.div
      style={{ opacity }}
      className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-20 pointer-events-none items-stretch gap-3"
    >
      <div className="relative w-[3px] h-[38vh] rounded-full overflow-hidden" style={{ background: 'var(--surface-100)' }}>
        <motion.div
          className="absolute inset-x-0 top-0 rounded-full origin-top"
          style={{ scaleY: fill, height: '100%', background: 'linear-gradient(180deg, var(--accent), #6ee7b7)' }}
        />
      </div>
      <div className="flex flex-col justify-between h-[38vh] py-0.5">
        {RAIL_STOPS.map((stop, i) => (
          <RailStop key={i} progress={scrollYProgress} at={stop.at} label={stop.label} />
        ))}
      </div>
    </motion.div>
  );
}
